import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  AreaChart,
  Area,
  YAxis,
  ResponsiveContainer
} from 'recharts';
import { formatCurrency } from '../../utils/formatters';
import { API_BASE_URL } from '../../config';
import './embed.css';

const EmbedMarket = ({ fullMarket = false, defaultShowCharts = true, boxStyle = false }) => {
  const [searchParams] = useSearchParams();

  // URL params for customization (can override fullMarket defaults)
  const theme = searchParams.get('theme') || 'dark';
  const bg = searchParams.get('bg') || (theme === 'dark' ? '#0a0a0a' : '#ffffff');
  const columns = parseInt(searchParams.get('cols')) || (fullMarket ? 0 : 2);
  const rows = parseInt(searchParams.get('rows')) || (fullMarket ? 0 : 3);
  const showChart = fullMarket
    ? (searchParams.get('chart') !== null ? searchParams.get('chart') !== 'false' : defaultShowCharts)
    : searchParams.get('chart') !== 'false';
  const showChange = searchParams.get('change') !== 'false';
  const showHeader = fullMarket && !boxStyle
    ? searchParams.get('header') === 'true'
    : searchParams.get('header') !== 'false';
  const layout = searchParams.get('layout') || 'grid';
  const refreshInterval = parseInt(searchParams.get('refresh')) || 10000;

  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);

  // For fullMarket, show all stocks. Otherwise cap at cols×rows.
  const maxItems = fullMarket ? 999 : columns * rows;

  const fetchStocks = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stocks`);
      if (response.ok) {
        const data = await response.json();
        const enriched = data.map(stock => ({
          ...stock,
          changePercent: stock.basePrice
            ? ((stock.currentPrice - stock.basePrice) / stock.basePrice) * 100
            : 0,
          chartData: (stock.history || []).slice(-20).map((h, i) => ({
            index: i,
            price: h.price || h.close
          }))
        }));
        setStocks(enriched.slice(0, maxItems));
      }
    } catch (error) {
      console.error('Error fetching stocks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStocks();

    socketRef.current = io(API_BASE_URL, {
      auth: { public: true }
    });

    socketRef.current.on('stock-updates', (updates) => {
      if (Array.isArray(updates)) {
        setStocks(prevStocks => {
          return prevStocks.map(stock => {
            const update = updates.find(u => u.id === stock.id || u.stockId === stock.id);
            if (update) {
              const newPrice = update.currentPrice || update.price;
              const newChartData = [...(stock.chartData || [])];
              newChartData.push({ index: newChartData.length, price: newPrice });
              const trimmedChartData = newChartData.slice(-30);
              return {
                ...stock,
                currentPrice: newPrice,
                changePercent: stock.basePrice
                  ? ((newPrice - stock.basePrice) / stock.basePrice) * 100
                  : stock.changePercent,
                chartData: trimmedChartData
              };
            }
            return stock;
          });
        });
      }
    });

    const interval = setInterval(fetchStocks, refreshInterval);

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      clearInterval(interval);
    };
  }, [refreshInterval, maxItems]);

  if (loading) {
    return (
      <div className="embed-container embed-market" style={{ background: bg }}>
        <div className="embed-loading">Loading market data...</div>
      </div>
    );
  }

  // Auto-calculate grid dimensions for full market
  const stockCount = stocks.length;
  let gridCols, gridRows;
  if (fullMarket && columns === 0) {
    // Auto-fit: wider ratio for charts, squarer for compact
    if (showChart) {
      gridCols = Math.ceil(Math.sqrt(stockCount * 2)); // wider cards with charts
    } else {
      gridCols = Math.ceil(Math.sqrt(stockCount * 1.5));
    }
    gridRows = Math.ceil(stockCount / gridCols);
  } else {
    gridCols = columns;
    gridRows = rows;
  }

  const getHeatmapColor = (changePercent) => {
    const max = 10;
    const t = Math.max(-1, Math.min(1, changePercent / max));
    const absT = Math.abs(t);
    // Neutral: dark slate
    const nr = 40, ng = 44, nb = 58;
    if (t >= 0) {
      return `rgb(${Math.round(nr + (0 - nr) * absT)}, ${Math.round(ng + (180 - ng) * absT)}, ${Math.round(nb + (60 - nb) * absT)})`;
    } else {
      return `rgb(${Math.round(nr + (220 - nr) * absT)}, ${Math.round(ng + (40 - ng) * absT)}, ${Math.round(nb + (40 - nb) * absT)})`;
    }
  };

  const MiniSparkline = ({ data, isPositive, stockId, compact }) => {
    if (!data || data.length < 2) return null;

    const color = isPositive ? '#00ff88' : '#ff4444';

    const prices = data.map(d => d.price).filter(p => p != null && !isNaN(p) && p > 0);
    if (prices.length === 0) return null;

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice;
    const avgPrice = (minPrice + maxPrice) / 2;

    const minRange = avgPrice * 0.05;
    const actualRange = Math.max(priceRange, minRange);
    const padding = actualRange * 0.2;

    const domain = [
      Math.max(0, avgPrice - actualRange / 2 - padding),
      avgPrice + actualRange / 2 + padding
    ];

    return (
      <ResponsiveContainer width="100%" height={compact ? 24 : 40}>
        <AreaChart data={data} margin={{ top: 1, right: 0, bottom: 1, left: 0 }}>
          <defs>
            <linearGradient id={`sparkGradient-${stockId}-${isPositive ? 'pos' : 'neg'}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <YAxis domain={domain} hide={true} />
          <Area
            type="monotone"
            dataKey="price"
            stroke={color}
            strokeWidth={compact ? 1 : 1.5}
            fill={`url(#sparkGradient-${stockId}-${isPositive ? 'pos' : 'neg'})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div
      className={`embed-container embed-market ${theme} layout-${layout}${fullMarket && !boxStyle ? ' heatmap' : ''}${boxStyle ? ' box' : ''}`}
      style={{
        background: bg === 'transparent' ? 'transparent' : bg,
        ...(fullMarket ? { height: '100vh', overflow: 'hidden' } : {}),
      }}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>
      {showHeader && (
        <div className="market-header">
          <span className="market-title">📊 MARKET OVERVIEW</span>
          <span className="market-count">{stocks.length} stocks</span>
        </div>
      )}

      <div
        className={`market-grid ${layout}`}
        style={{
          gridTemplateColumns: layout === 'grid' ? `repeat(${gridCols}, 1fr)` : '1fr',
          ...(fullMarket ? {
            height: showHeader ? 'calc(100vh - 50px)' : '100vh',
            gridTemplateRows: `repeat(${gridRows}, 1fr)`,
            overflow: 'hidden',
          } : {}),
        }}
      >
        {stocks.map((stock) => {
          const isPositive = stock.changePercent >= 0;

          return (
            <div
              key={stock.id}
              className={`market-card${fullMarket && !boxStyle ? '' : boxStyle ? '' : ` ${isPositive ? 'positive' : 'negative'}`}`}
              style={fullMarket ? {
                padding: showChart ? '3px 6px' : '4px 8px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: 0,
                overflow: 'hidden',
                ...(boxStyle ? {} : { background: getHeatmapColor(stock.changePercent) }),
              } : {}}
            >
              <div className="card-header">
                <span className="card-ticker" style={fullMarket ? { fontSize: 'clamp(8px, 1.3vw, 36px)' } : {}}>{stock.ticker}</span>
                {showChange && (
                  <span
                    className={`card-change ${isPositive ? 'positive' : 'negative'}`}
                    style={fullMarket ? { fontSize: 'clamp(7px, 1vw, 28px)' } : {}}
                  >
                    {isPositive ? '+' : ''}{stock.changePercent.toFixed(1)}%
                  </span>
                )}
              </div>

              <div className="card-price" style={fullMarket ? { fontSize: 'clamp(9px, 1.4vw, 36px)', marginBottom: showChart ? '1px' : 0 } : {}}>
                {formatCurrency(stock.currentPrice)}
              </div>

              {showChart && stock.chartData && stock.chartData.length > 1 && (
                <div className="card-chart" style={fullMarket ? { flex: 1, minHeight: 0 } : {}}>
                  <MiniSparkline data={stock.chartData} isPositive={isPositive} stockId={stock.id} compact={fullMarket} />
                </div>
              )}

              {layout === 'list' && (
                <div className="card-details">
                  <span className="card-name">{stock.name}</span>
                  <span className="card-base">Base: {formatCurrency(stock.basePrice)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EmbedMarket;
